import React, {Fragment, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

// Components
import {Typography} from '@nur-portal/ui-kit';

import {CommentsEventLogger} from '../../../../services/analytics/comments';
import {Button} from '../../../../website-ui/components/button';
import CommentCard from '../../../../website-ui/components/comments/comment-card/commentCard';
import CommentThread from '../../../../website-ui/components/comments/comment-thread/comment-thread';
import {
  COMMENTS_MODAL_SEARCH_PARAM_VALUE,
  MODAL_SEARCH_PARAM_KEY} from '../../../../website-ui/constants/url';
import {ReactionsProvider} from '../../../../website-ui/contexts/reactions-provider';
import {useUser} from '../../../../website-ui/hooks/use-user';
// Tools
import {useArticleModel} from '../../../../website-ui/utils/shared/useArticleModel';
import CommentReactions from '../../reactions/components/comment-reactions';
import {useReadReactions} from '../../reactions/useReadReactions';
import {LoaderCard} from '../loader-card/loader-card';
import Comment from '../modal-comments/comment';

import {useArticlePreviewComments} from './hook/useArticlePreviewComments';
import CommentAreaHeader from './comment-area-header';

// Styles
import styles from './article-preview.module.css';

export const ArticlePreviewWrapper = ({articleId}) => {
  const {t} = useTranslation();
  const {
    comments,
    isShowLoader
  } = useArticlePreviewComments(articleId);

  const article = useArticleModel(articleId);

  const user = useUser();

  useReadReactions({
    articleId,
    user,
    shouldReactionsFetch: comments && (comments.length > 0)
  });

  const handleButtonClick = useCallback(() => {
    CommentsEventLogger.clickCommentsButton(articleId);
    const url = new URL(article.url);

    url.searchParams.set(MODAL_SEARCH_PARAM_KEY, COMMENTS_MODAL_SEARCH_PARAM_VALUE);
    window.history.pushState({id: articleId}, null, url);
    const navEvent = new PopStateEvent('popstate');

    window.dispatchEvent(navEvent);
  }, [articleId]);

  if (isShowLoader) {
    return (
      <div className={styles.wrapper}>
        <LoaderCard />
      </div>
    );
  }

  return (
    <ReactionsProvider articleId={articleId}>
      <div className={styles.wrapper}>
        <CommentAreaHeader
          commentsCount={article.commentsCount || 0}
          isUserAuthenticated={user.isAuthenticated}
        />
        {comments?.length >= 1 && (
          <div className={styles.commentArea}>
            {comments.slice(0, 2).map((comment, indexComment) => (
              <div key={comment.id}>
                <CommentCard
                  text={comment.text}
                  date={comment.createdAt}
                  userName={comment.user.username}
                >
                  <CommentReactions
                    articleId={articleId}
                    commentId={comment.id}
                    isFromPreview
                  />
                </CommentCard>
                {comment.replies.length > 0 && (
                  <div className={styles.hiddenComments}>
                    {comment.replies.map(commentReply => (
                      <CommentThread
                        key={commentReply.id}
                        hideReactionsBlock
                        articleId={articleId}
                        comment={commentReply}
                        level={2}
                        isLast={indexComment === comments.length - 1}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {comments?.length >= 1 && (
          <div className={styles.hiddenComments}>
            {comments.slice(2).map((comment, indexComment) => (
              <Fragment key={comment.id}>
                <Comment
                  hideReactionsBlock
                  articleId={articleId}
                  comment={comment}
                  level={1}
                  isLast={indexComment === comments.length - 1 && comment.replies.length === 0}
                />
                {comment.replies.length > 0 && comment.replies.map(commentReply => (
                  <CommentThread
                    key={commentReply.id}
                    hideReactionsBlock
                    articleId={articleId}
                    comment={commentReply}
                    level={2}
                    isLast={indexComment === comments.length - 1}
                  />
                ))}
              </Fragment>
            ))}
          </div>
        )}
        <div className={styles.buttonWrapper}>
          <Button
            variant='primary'
            onClick={handleButtonClick}
            className={styles.button}
          >
            <Typography
              variant='text-14'
              className={styles.buttonText}
            >
              {
                comments?.length >= 1
                  ? t('articleComments.goToThread')
                  : t('articleComments.leaveFirstComment')
              }
            </Typography>
          </Button>
        </div>
      </div>
    </ReactionsProvider>
  );
};
